/**
 * @file Catalogue des auras FX (effet constant autour du chat, purement cosmetique, vendues UNIQUEMENT en boutique) : des plus simples aux plus folles.
 * Ajouter une aura = une entree de donnees ; le rendu lit `style` sans connaitre l'aura.
 */
import type { AuraDef, AuraRig, RigOrbiter } from './types';

/** Raccourci : aura « orbites » (RGB) — `colors` ne sert qu'a l'apercu de la boutique. */
const rig = (id: string, fr: string, en: string, cost: AuraDef['cost'], colors: [string, string], r: AuraRig): AuraDef => ({
  id,
  name: { fr, en },
  cost,
  style: { form: 'orbit', colors, particle: 'dot', density: 5, rainbow: r.orbiters.some((o) => (o.color ?? 'rgb') === 'rgb') || r.core?.color === 'rgb', rig: r },
});
const orb = (o: RigOrbiter): RigOrbiter => o;

export const AURAS: readonly AuraDef[] = [
  { id: 'aura_glow', name: { fr: 'Lueur douce', en: 'Soft glow' }, cost: { dia: 200 }, style: { form: 'glow', colors: ['#fff2b0', '#ffffff'], particle: 'dot', density: 1 } },
  { id: 'aura_bubbles', name: { fr: 'Bulles', en: 'Bubbles' }, cost: { dia: 400 }, style: { form: 'rise', colors: ['#9fe4ff', '#ffffff'], particle: 'bubble', density: 2 } },
  { id: 'aura_petals', name: { fr: 'Petales', en: 'Petals' }, cost: { dia: 600 }, style: { form: 'spiral', colors: ['#ffb3d1', '#ffffff'], particle: 'petal', density: 2 } },
  { id: 'aura_hearts', name: { fr: 'Coeurs', en: 'Hearts' }, cost: { dia: 900 }, style: { form: 'rise', colors: ['#ff5f9a', '#ffc2da'], particle: 'heart', density: 3 } },
  { id: 'aura_snow', name: { fr: 'Flocons', en: 'Snowfall' }, cost: { dia: 1200 }, style: { form: 'orbit', colors: ['#cfeeff', '#ffffff'], particle: 'snow', density: 3, groundRing: true } },
  { id: 'aura_stars', name: { fr: 'Etoiles', en: 'Stars' }, cost: { dia: 1600 }, style: { form: 'orbit', colors: ['#ffd84a', '#a78bff'], particle: 'star', density: 3, groundRing: true } },
  { id: 'aura_notes', name: { fr: 'Melodie', en: 'Melody' }, cost: { dia: 2000 }, style: { form: 'spiral', colors: ['#7ff0d0', '#ffd84a'], particle: 'note', density: 3, groundRing: true } },
  { id: 'aura_flames', name: { fr: 'Flammes', en: 'Flames' }, cost: { dia: 2600 }, style: { form: 'ring', colors: ['#ff7a2e', '#ffd23c'], particle: 'flame', density: 4, groundRing: true } },
  { id: 'aura_storm', name: { fr: 'Orage', en: 'Storm' }, cost: { dia: 3500 }, style: { form: 'burst', colors: ['#7fdcff', '#fff7a8'], particle: 'bolt', density: 4, groundRing: true } },
  // ---- orbites RGB : gemmes
  rig('aura_rgb_orbit', 'Orbite RGB', 'RGB orbit', { dia: 4000 }, ['#ff3b3b', '#3b6aff'], {
    orbiters: [orb({ path: 'circle', count: 3, radius: 1.15, height: 0.95, speed: 2.4, size: 0.2, shape: 'orb', trail: 0.7, hueSpread: 0.33 })],
    ground: 'rings',
  }),
  rig('aura_gyro', 'Gyroscope', 'Gyroscope', { dia: 4500 }, ['#33f5ff', '#ff3df0'], {
    orbiters: [
      orb({ path: 'tilted', count: 1, radius: 1.2, height: 0.95, speed: 2.8, size: 0.19, shape: 'orb', trail: 0.8, tilt: 0, yaw: 0, hueSpread: 0 }),
      orb({ path: 'tilted', count: 1, radius: 1.2, height: 0.95, speed: -2.4, size: 0.19, shape: 'orb', trail: 0.8, tilt: 1.05, yaw: 0, hueSpread: 0.33 }),
      orb({ path: 'tilted', count: 1, radius: 1.2, height: 0.95, speed: 2.1, size: 0.19, shape: 'orb', trail: 0.8, tilt: 1.05, yaw: 2.1, hueSpread: 0.66 }),
    ],
    ground: 'rune',
  }),
  rig('aura_atom', 'Atome', 'Atom', { dia: 5000 }, ['#7aa8ff', '#ffd84a'], {
    orbiters: [
      orb({ path: 'ellipse', count: 1, radius: 1.4, height: 0.95, speed: 3.2, size: 0.17, shape: 'orb', trail: 0.9, aspect: 0.42, tilt: 0.5, yaw: 0, hueSpread: 0 }),
      orb({ path: 'ellipse', count: 1, radius: 1.4, height: 0.95, speed: 3.2, size: 0.17, shape: 'orb', trail: 0.9, aspect: 0.42, tilt: 0.5, yaw: 1.05, hueSpread: 0.33 }),
      orb({ path: 'ellipse', count: 1, radius: 1.4, height: 0.95, speed: 3.2, size: 0.17, shape: 'orb', trail: 0.9, aspect: 0.42, tilt: 0.5, yaw: 2.1, hueSpread: 0.66 }),
    ],
    core: { size: 0.32, kind: 'glow', color: 'rgb' },
  }),
  rig('aura_dna', 'Double helice', 'Double helix', { dia: 5500 }, ['#4fe0ff', '#ff4fa0'], {
    orbiters: [
      orb({ path: 'helix', count: 7, radius: 0.75, height: 0, speed: 2.6, size: 0.12, shape: 'orb', trail: 0.35, rise: 0.45, color: 'a', yaw: 0 }),
      orb({ path: 'helix', count: 7, radius: 0.75, height: 0, speed: 2.6, size: 0.12, shape: 'orb', trail: 0.35, rise: 0.45, color: 'b', yaw: Math.PI }),
    ],
    ground: 'pulse',
  }),
  rig('aura_spiro', 'Spirographe', 'Spirograph', { dia: 6000 }, ['#ff6bd0', '#6bf0ff'], {
    orbiters: [
      orb({ path: 'lissajous', count: 1, radius: 1.3, height: 1.0, speed: 2.2, size: 0.17, shape: 'orb', trail: 1.0, aspect: 1.5, rise: 0.4, width: 1.1 }),
      orb({ path: 'lissajous', count: 1, radius: 1.3, height: 1.0, speed: 2.2, size: 0.17, shape: 'orb', trail: 1.0, aspect: 1.5, rise: 0.4, width: 1.1, yaw: Math.PI, hueSpread: 0.5 }),
    ],
  }),
  rig('aura_tornado', 'Tornade RGB', 'RGB tornado', { dia: 6500 }, ['#ff4fd8', '#4fffd8'], {
    orbiters: [orb({ path: 'tornado', count: 22, radius: 1.25, height: 0, speed: 4.2, size: 0.1, shape: 'orb', trail: 0.25, rise: 0.55, hueSpread: 1 })],
    ground: 'vortex',
  }),
  rig('aura_satellites', 'Satellites', 'Satellites', { dia: 7000 }, ['#ffd84a', '#4fe0ff'], {
    orbiters: [
      orb({ path: 'circle', count: 6, radius: 1.3, height: 1.95, speed: 1.4, size: 0.16, shape: 'diamond', trail: 0.3, hueSpread: 1 }),
      orb({ path: 'circle', count: 3, radius: 0.85, height: 0.35, speed: -2.0, size: 0.12, shape: 'orb', trail: 0.5, hueSpread: 1 }),
    ],
    ground: 'star',
  }),
  rig('aura_comets', 'Cometes', 'Comets', { dia: 7500 }, ['#fff2a8', '#ff7ad9'], {
    orbiters: [orb({ path: 'comet', count: 4, radius: 1.75, height: 1.05, speed: 2.0, size: 0.22, shape: 'star', trail: 1.0, aspect: 0.55, tilt: 0.35, hueSpread: 0.7, width: 1.2 })],
  }),
  rig('aura_infinity', 'Infini', 'Infinity', { dia: 8000 }, ['#ff3b8a', '#3bd0ff'], {
    orbiters: [
      orb({ path: 'eight', count: 2, radius: 1.4, height: 0.9, speed: 2.4, size: 0.18, shape: 'orb', trail: 0.95, hueSpread: 0.5 }),
    ],
    ground: 'rings',
  }),
  rig('aura_pulse', 'Impulsion', 'Pulse', { dia: 8500 }, ['#4fff9a', '#4f9aff'], {
    orbiters: [orb({ path: 'fountain', count: 10, radius: 0.7, height: 0, speed: 1.2, size: 0.12, shape: 'orb', trail: 0.3, rise: 0.9, hueSpread: 1 })],
    ground: 'pulse',
  }),
  { id: 'aura_neon', name: { fr: 'Neon', en: 'Neon' }, cost: { ruby: 40 }, style: { form: 'ring', colors: ['#33f5ff', '#ff3df0'], particle: 'dot', density: 4, groundRing: true, pillar: true } },
  { id: 'aura_inferno', name: { fr: 'Brasier', en: 'Inferno' }, cost: { ruby: 50 }, style: { form: 'ring', colors: ['#ff4a1a', '#ffd23c'], particle: 'flame', density: 5, groundRing: true, pillar: true, extra: 'inferno' } },
  { id: 'aura_thunder', name: { fr: 'Foudre', en: 'Thunderbolt' }, cost: { ruby: 70 }, style: { form: 'burst', colors: ['#7fc8ff', '#ffffff'], particle: 'bolt', density: 5, groundRing: true, pillar: true, extra: 'lightning' } },
  { id: 'aura_rainbow', name: { fr: 'Arc-en-ciel', en: 'Rainbow' }, cost: { ruby: 80 }, style: { form: 'spiral', colors: ['#ff6b6b', '#6b9bff'], particle: 'star', density: 5, rainbow: true, groundRing: true, pillar: true } },
  { id: 'aura_cosmic', name: { fr: 'Cosmique', en: 'Cosmic' }, cost: { ruby: 150 }, style: { form: 'burst', colors: ['#a78bff', '#ffd84a'], particle: 'star', density: 5, rainbow: true, groundRing: true, pillar: true } },
  // ---- orbites RGB : rubis
  rig('aura_angel', 'Ange', 'Angel', { ruby: 90 }, ['#fff2b0', '#ffffff'], {
    orbiters: [
      orb({ path: 'crown', count: 10, radius: 0.55, height: 2.1, speed: 1.6, size: 0.1, shape: 'orb', trail: 0.4, color: 'a' }),
      orb({ path: 'fountain', count: 8, radius: 0.9, height: 0, speed: 0.8, size: 0.2, shape: 'petal', trail: 0, rise: 0.35, color: 'white' }),
    ],
    ground: 'star',
    core: { size: 0.42, kind: 'glow', color: 'a' },
  }),
  rig('aura_crown', 'Couronne RGB', 'RGB crown', { ruby: 100 }, ['#ff5a5a', '#5a8aff'], {
    orbiters: [
      orb({ path: 'crown', count: 7, radius: 0.5, height: 2.05, speed: 1.2, size: 0.17, shape: 'diamond', trail: 0.25, hueSpread: 1 }),
      orb({ path: 'circle', count: 5, radius: 1.3, height: 0.5, speed: -1.1, size: 0.1, shape: 'star', trail: 0.5, hueSpread: 1 }),
    ],
    ground: 'rings',
  }),
  rig('aura_blades', 'Barriere de lames', 'Blade barrier', { ruby: 110 }, ['#7fe8ff', '#ff5ad2'], {
    orbiters: [
      orb({ path: 'blades', count: 8, radius: 1.05, height: 0.85, speed: 4.2, size: 0.3, shape: 'blade', trail: 0.4, hueSpread: 1 }),
      orb({ path: 'blades', count: 5, radius: 1.45, height: 1.2, speed: -3.0, size: 0.22, shape: 'blade', trail: 0.35, hueSpread: 1 }),
    ],
    ground: 'rune',
  }),
  rig('aura_butterflies', 'Papillons', 'Butterflies', { ruby: 120 }, ['#ff9ad8', '#9ae8ff'], {
    orbiters: [orb({ path: 'flutter', count: 7, radius: 1.5, height: 1.0, speed: 1.6, size: 0.22, shape: 'petal', trail: 0.65, hueSpread: 1, width: 0.7 })],
    ground: 'lotus',
  }),
  rig('aura_fountain', 'Fontaine d\'etoiles', 'Star fountain', { ruby: 135 }, ['#ffd84a', '#ff5ad2'], {
    orbiters: [orb({ path: 'fountain', count: 16, radius: 1.5, height: 0, speed: 2.0, size: 0.17, shape: 'star', trail: 0.45, rise: 1.1, hueSpread: 1 })],
    ground: 'pulse',
  }),
  rig('aura_matrix', 'Pluie numerique', 'Digital rain', { ruby: 150 }, ['#3dff7a', '#19f0ff'], {
    orbiters: [
      orb({ path: 'rain', count: 26, radius: 1.15, height: 0, speed: 1.0, size: 0.1, shape: 'square', trail: 0.55, rise: 1.8, color: 'a', hueSpread: 0, width: 0.6 }),
      orb({ path: 'rain', count: 12, radius: 0.8, height: 0, speed: -1.0, size: 0.08, shape: 'square', trail: 0.4, rise: 1.3, color: 'b', width: 0.5 }),
    ],
    ground: 'checker',
  }),
  rig('aura_serpent', 'Serpent dragon', 'Dragon serpent', { ruby: 180 }, ['#4fff7a', '#ff4f4f'], {
    orbiters: [orb({ path: 'snake', count: 26, radius: 1.2, height: 1.0, speed: 2.1, size: 0.17, shape: 'orb', trail: 0.12, hueSpread: 0.9, rise: 0.7 })],
    ground: 'vortex',
  }),
  rig('aura_solar', 'Systeme solaire', 'Solar system', { ruby: 220 }, ['#ffd84a', '#4f9aff'], {
    orbiters: [
      orb({ path: 'circle', count: 1, radius: 0.65, height: 0.95, speed: 4.0, size: 0.11, shape: 'orb', trail: 0.4, hueSpread: 0 }),
      orb({ path: 'tilted', count: 1, radius: 1.0, height: 0.95, speed: 2.7, size: 0.15, shape: 'orb', trail: 0.5, tilt: 0.3, yaw: 0.8, hueSpread: 0.2 }),
      orb({ path: 'tilted', count: 1, radius: 1.4, height: 0.95, speed: 1.9, size: 0.19, shape: 'orb', trail: 0.55, tilt: -0.25, yaw: 2.0, hueSpread: 0.45 }),
      orb({ path: 'tilted', count: 2, radius: 1.85, height: 0.95, speed: 1.2, size: 0.15, shape: 'orb', trail: 0.6, tilt: 0.2, yaw: 3.4, hueSpread: 0.8 }),
    ],
    core: { size: 0.38, kind: 'glow', color: 'a' },
    ground: 'rings',
  }),
  rig('aura_mandala', 'Mandala', 'Mandala', { ruby: 260 }, ['#ff5ad2', '#5af0ff'], {
    orbiters: [
      orb({ path: 'fountain', count: 14, radius: 1.0, height: 0, speed: 1.0, size: 0.2, shape: 'petal', trail: 0, rise: 0.3, hueSpread: 1 }),
      orb({ path: 'circle', count: 8, radius: 1.6, height: 0.05, speed: 0.9, size: 0.12, shape: 'orb', trail: 0.4, hueSpread: 1 }),
    ],
    ground: 'mandala',
  }),
  rig('aura_singularity', 'Singularite', 'Singularity', { ruby: 400 }, ['#b36bff', '#ffb36b'], {
    orbiters: [
      orb({ path: 'tilted', count: 18, radius: 1.35, height: 1.0, speed: 3.6, size: 0.09, shape: 'orb', trail: 0.5, tilt: 0.42, yaw: 0.3, hueSpread: 0.45 }),
      orb({ path: 'tilted', count: 10, radius: 0.85, height: 1.0, speed: 5.0, size: 0.07, shape: 'orb', trail: 0.4, tilt: 0.42, yaw: 0.3, hueSpread: 0.45 }),
    ],
    core: { size: 0.34, kind: 'dark' },
    ground: 'vortex',
  }),
];
